import { loadQuery, type PreloadedQuery } from 'react-relay';
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
// eslint-disable-next-line jira/import-whitelist
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import getEditions from '@atlassian/jira-common-util-get-tenant-context/src/edition/index.tsx';
import getLicenseStates from '@atlassian/jira-common-util-get-tenant-context/src/license-state/index.tsx';
import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import workItemViewResourcesQuery, {
	type JiraResourceIntegration,
	type workItemViewResourcesQuery as WorkItemViewResourcesQueryType,
	type workItemViewResourcesQuery$variables,
} from './__generated__/workItemViewResourcesQuery.graphql.ts';

const RESOURCES_INTEGRATION: Record<string, JiraResourceIntegration> = {
	ATTACHMENT: 'ATTACHMENT',
	LOOM: 'LOOM',
	CONFLUENCE: 'CONFLUENCE',
	WHITEBOARD: 'WHITEBOARD',
	GDRIVE: 'GDRIVE',
	WEBLINK: 'WEBLINK',
};

const hasActiveJiraSoftwareLicense = (): boolean =>
	getLicensedProducts(getMeta)[LICENSED_PRODUCTS.JIRA_SOFTWARE] === true &&
	getLicenseStates(getMeta).software === LICENSE_STATE_ACTIVE;

export const getWorkItemResourcesQueryVariablesForIssueKey = (
	issueKey: string | null | undefined,
	storeContext: ResourceStoreContext,
): workItemViewResourcesQuery$variables | null => {
	if (!issueKey) {
		return null;
	}

	const {
		tenantContext: { cloudId },
	} = storeContext;

	return {
		issueKey,
		cloudId,
		first: 10, // DEFAULT_GRID_COLUMNS(5) * GRID_INITIAL_ROWS(2)
		hydrate: true,
		orderField: 'CREATED',
		orderDirection: 'DESC',
		applicationKey: 'JIRA_SOFTWARE',
		issueViewAddAttachmentFlag:
			getEditions(getMeta).software === 'FREE_EDITION' &&
			getLicenseStates(getMeta).software === 'ACTIVE',
		productKey: 'jira-software',
		// Gates a backend-sensitive TWC entitlement lookup that requires an active Jira Software license.
		isTwcEntitlementCheckEnabled:
			hasActiveJiraSoftwareLicense() && fg('twcg-1399_twc_project_kickoff_workflow_killswitch'),
		// Gates a backend-sensitive TWC co-use entitlement lookup that requires an active Jira Software
		// license, so the fetch does not fire on non-JSW issue views.
		isTwcCoUseRovoCheckEnabled:
			hasActiveJiraSoftwareLicense() && fg('twcg_1846_twc_couse_jira_pt_rovo_create_killswitch'),
		filters: [
			{ integration: RESOURCES_INTEGRATION.CONFLUENCE },
			{ integration: RESOURCES_INTEGRATION.WHITEBOARD },
			{ integration: RESOURCES_INTEGRATION.LOOM },
			{ integration: RESOURCES_INTEGRATION.ATTACHMENT, types: [] },
			{ integration: RESOURCES_INTEGRATION.WEBLINK },
		],
	};
};

export const getNonNullWorkItemResourcesQueryVariablesForIssueKey = (
	issueKey: string | null | undefined,
	cloudId: string | null | undefined,
): workItemViewResourcesQuery$variables => {
	return {
		issueKey: issueKey || '',
		cloudId: cloudId || '',
		first: 10, // DEFAULT_GRID_COLUMNS(5) * GRID_INITIAL_ROWS(2)
		hydrate: true,
		orderField: 'CREATED',
		orderDirection: 'DESC',
		applicationKey: 'JIRA_SOFTWARE',
		issueViewAddAttachmentFlag:
			getEditions(getMeta).software === 'FREE_EDITION' &&
			getLicenseStates(getMeta).software === 'ACTIVE',
		productKey: 'jira-software',
		// Gates a backend-sensitive TWC entitlement lookup that requires an active Jira Software license.
		isTwcEntitlementCheckEnabled:
			hasActiveJiraSoftwareLicense() && fg('twcg-1399_twc_project_kickoff_workflow_killswitch'),
		// Gates a backend-sensitive TWC co-use entitlement lookup that requires an active Jira Software
		// license, so the fetch does not fire on non-JSW issue views.
		isTwcCoUseRovoCheckEnabled:
			hasActiveJiraSoftwareLicense() && fg('twcg_1846_twc_couse_jira_pt_rovo_create_killswitch'),
		filters: [
			{ integration: RESOURCES_INTEGRATION.CONFLUENCE },
			{ integration: RESOURCES_INTEGRATION.WHITEBOARD },
			{ integration: RESOURCES_INTEGRATION.LOOM },
			{ integration: RESOURCES_INTEGRATION.ATTACHMENT, types: [] },
			{ integration: RESOURCES_INTEGRATION.WEBLINK },
		],
	};
};

export const workItemResourcesDataFetcher = async (
	routerContext: RouterDataContext,
	storeContext: ResourceStoreContext,
): Promise<PreloadedQuery<WorkItemViewResourcesQueryType, {}> | null> => {
	const variables = getWorkItemResourcesQueryVariablesForIssueKey(
		getIssueKey(routerContext.match),
		storeContext,
	);

	if (!variables) {
		return null;
	}

	const workItemViewResourcesData = loadQuery<WorkItemViewResourcesQueryType>(
		getRelayEnvironment(),
		workItemViewResourcesQuery,
		variables,
	);
	return workItemViewResourcesData;
};
